import React from 'react';
import { getValue, renderValue } from '../../../../components/cardDetails/valueRender';
import { splitCamelCase } from '../../../../components/cardDetails/cardValues';
import { CardConfig } from '../../../../utils/artDetailsUtils';
import { useSnapshot } from 'valtio';
import appModel from '../../../../models/app-model';
import formatDateString from '../../../../utils/formatted-date';

// Styles
import styles from '../artDetails.modal.module.css';

type Label = { val: string, grp: number, lbl: string }

type ContactData = {
  document: {
    notificationsEmail?: string;
    contactName?: string;
    contactPhone?: string;
  };
};

type ArtDetailsContentProps = {
  labelMap: Map<number, Label[]>;
  mappedCards: CardConfig[];
  unmappedKeys: string[];
  labelKeys: Set<string>;
  wizardProps: any;
  viewMoreDetails: boolean;
  contactData: ContactData;
};

export interface WizardDocData {
  addedToCartDate: string;
  submittedDate: string;
  source: string;
}

export const docLabelMap: Record<keyof WizardDocData, string> = {
  source: "Source",
  submittedDate: "Date Art Submitted",
  addedToCartDate: "Last Viewed"
};

export const globalDiv = document.createElement('div');
export function htmlDecode(content: string) {
  globalDiv.innerHTML = content;
  const val = globalDiv.childNodes.length === 0 ? "" : globalDiv.childNodes[0].nodeValue;
  return val ?? ""
}

const ArtDetailsContent: React.FC<ArtDetailsContentProps> = ({
  labelMap,
  mappedCards,
  unmappedKeys,
  labelKeys,
  wizardProps,
  viewMoreDetails,
  contactData
}) => {
  const appSnap = useSnapshot(appModel);
  const doc = appSnap.activeDesignDoc;
  const wizardDocData: WizardDocData = {
    addedToCartDate: doc?.addedToCartDate ? formatDateString({ dateString: doc?.addedToCartDate }) : "--",
    submittedDate: doc?.createdDate ? formatDateString({ dateString: doc?.createdDate }) : "--",
    source: doc?.docType ? splitCamelCase(doc?.docType) : "--"
  };

  const contactInfoKeys = new Set(['notificationsEmail', 'contactName', 'contactPhone']);
  const filteredUnmappedKeys = unmappedKeys.filter(key => !contactInfoKeys.has(key));

  const renderContactInfo = () => {
    const contactFields = [];

    if (contactData.document.notificationsEmail) {
      contactFields.push({
        key: 'notificationsEmail',
        label: 'Notifications Email',
        value: contactData.document.notificationsEmail
      });
    }

    if (contactData.document.contactName) {
      contactFields.push({
        key: 'contactName',
        label: 'Contact Name',
        value: contactData.document.contactName
      });
    }

    if (contactData.document.contactPhone) {
      contactFields.push({
        key: 'contactPhone',
        label: 'Contact Phone',
        value: contactData.document.contactPhone
      });
    }

    if (contactFields.length === 0) return null;

    return (
      <div className={styles.detailItem} key="contact-info-group">
        {contactFields.map((field) => {
          const displayValue = renderValue(field.value);
          if (displayValue === null) return null;

          return (
            <div key={field.key} className={styles.lblWrapper}>
              <span className={styles.label}>{field.label}:</span>
              <span className={styles.artValue}>{displayValue}</span>
            </div>
          );
        })}
      </div>
    );
  };

  return <>
    {Array.from(labelMap ?? []).map(([grpId, labels]) => {
      const validLabels = labels.filter(label => label.val && label.lbl);
      if (validLabels.length === 0) return null;

      return (
        <div className={styles.detailItem} key={grpId}>
          {validLabels.map((label, idx) => (
            <div key={idx} className={styles.lblWrapper}>
              <span className={styles.label}>
                {htmlDecode(label.lbl)}:
              </span>
              <span className={styles.artValue} dangerouslySetInnerHTML={{ __html: label.val }} />
            </div>
          ))}
        </div>
      );
    })}

    {/* {Render doc data}*/}
    {Object.entries(wizardDocData).map(([key, value]) => (
      <div className={styles.detailItem} key={key}>
        <span className={styles.label}>{docLabelMap[key as keyof WizardDocData]}:</span>
        <span className={styles.artValue}>{value}</span>
      </div>
    ))}

    {/* Render mapped keys */}
    {mappedCards?.map((cardConfig, index) => {
      const filteredProperties = cardConfig.properties.filter(
        property => !labelKeys.has(String(property.key)) && !contactInfoKeys.has(String(property.key))
      );

      if (filteredProperties.length === 0) return null;

      return (
        <React.Fragment key={index}>
          {filteredProperties.map((property) => {
            const value = getValue(property, wizardProps, "");
            const displayValue = renderValue(value);

            if (displayValue === null) return null;

            return (
              <div className={styles.detailItem} key={property.key}>
                <span className={styles.label}>{property.label}:</span>
                <span className={styles.artValue}>{displayValue}</span>
              </div>
            );
          })}
        </React.Fragment>
      );
    })}

    {/* Render contact information grouped */}
    {renderContactInfo()}

    {/* Render unmapped keys (excluding contact info) */}
    {viewMoreDetails && filteredUnmappedKeys?.map(key => {
      const value = wizardProps[key];
      const displayValue = renderValue(value);

      if (displayValue === null) return null;

      return (
        <div className={styles.detailItem} key={key}>
          <span className={styles.label}>{splitCamelCase(key)}:</span>
          <span className={styles.artValue}>{displayValue}</span>
        </div>
      );
    })}
  </>
};

export default ArtDetailsContent;